import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, DestroyRef, EventEmitter, Inject, Input, OnInit, Optional, Output, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { SelectionModel } from '@angular/cdk/collections';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { backendEndPointToken } from '../../app.config';
import { Bundle, ImagingStudy } from 'fhir/r4';
import { forkJoin, map, of, catchError, tap } from 'rxjs';
import { StateService } from '../state.service';

export interface ImagingStudyRow {
    imagingStudy: ImagingStudy;
}

export interface RequiredImagingStudyResultsTableDialogData {
    serviceRequestIds: string[];
    patientId?: string | null;
    forSelection?: boolean;
    imagingStudiesToMatch?: ImagingStudy[];
}

@Component({
    selector: 'app-required-imaging-study-results-table',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatTableModule,
        MatCheckboxModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule
    ],
    templateUrl: './required-imaging-study-results-table.component.html',
    styleUrl: './required-imaging-study-results-table.component.scss'
})
export class RequiredImagingStudyResultsTableComponent implements OnInit {
    @Input() serviceRequestIds: string[] = [];
    @Input() patientId?: string | null;
    @Input() forSelection = true;
    @Input() imagingStudiesToMatch: ImagingStudy[] = [];

    @Output() selectionChange = new EventEmitter<ImagingStudyRow[]>();

    private readonly http = inject(HttpClient);
    private readonly backendUrl = inject(backendEndPointToken);
    private readonly stateService = inject(StateService);
    private readonly destroyRef = inject(DestroyRef);

    private readonly dialogData = inject(MAT_DIALOG_DATA, { optional: true }) as RequiredImagingStudyResultsTableDialogData | null;
    private readonly dialogRef = inject(MatDialogRef<RequiredImagingStudyResultsTableComponent>, { optional: true }) as MatDialogRef<RequiredImagingStudyResultsTableComponent> | null;

    dataSource = new MatTableDataSource<ImagingStudyRow>([]);
    selection = new SelectionModel<ImagingStudyRow>(true, []);

    loading = false;
    filterText = '';

    tableColumns: string[] = ['select', 'description', 'status', 'modality', 'started'];

    constructor() {
        if (!this.dialogData) return;
        this.serviceRequestIds = this.dialogData.serviceRequestIds ?? [];
        this.patientId = this.dialogData.patientId ?? null;
        this.forSelection = this.dialogData.forSelection ?? true;
        this.imagingStudiesToMatch = this.dialogData.imagingStudiesToMatch ?? [];
    }

    ngOnInit(): void {
        if (!this.forSelection) {
            this.tableColumns = ['description', 'status', 'modality', 'started'];
        }

        this.selection.changed
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.selectionChange.emit([...this.selection.selected]));

        this.fetch();
    }

    close(): void {
        this.dialogRef?.close();
    }

    confirmSelection(): void {
        this.dialogRef?.close({ rows: [...this.selection.selected] });
    }

    applyFilter(value: string): void {
        this.filterText = value ?? '';
        const v = this.filterText.trim().toLowerCase();
        this.dataSource.filter = v;
    }

    fetch(): void {
        const ids = Array.from(new Set((this.serviceRequestIds ?? []).map((x) => (x ?? '').toString().trim()).filter(Boolean)));
        if (!ids.length) {
            this.dataSource.data = [];
            return;
        }

        this.loading = true;

        const subjectParam = this.patientId ? `subject=${encodeURIComponent(`Patient/${this.patientId}`)}&` : '';

        forkJoin(
            ids.map((id) => {
                const url = `${this.backendUrl}/ImagingStudy?${subjectParam}basedon=${encodeURIComponent(`ServiceRequest/${id}`)}&_count=200`;
                return this.http.get<Bundle<ImagingStudy>>(url, { headers: { Accept: 'application/fhir+json' } }).pipe(
                    tap((b: any) => {
                        if (!b) return;
                        this.stateService.processOrgWideBundleTransaction(b as any);
                        const current = this.stateService.currentPatientId$.getValue();
                        if (this.patientId && current && current === this.patientId) {
                            this.stateService.processBundleTransaction(b as any);
                        }
                    }),
                    catchError(() => of({ entry: [] } as any))
                );
            })
        )
            .pipe(
                map((bundles: any[]) => {
                    const resources = bundles
                        .flatMap((b) => (b?.entry ?? []).map((e: any) => e?.resource))
                        .filter(Boolean)
                        .filter((r: any) => r?.resourceType === 'ImagingStudy') as ImagingStudy[];

                    const byId = new Map<string, ImagingStudy>();
                    for (const r of resources) {
                        const id = (r?.id ?? '').toString();
                        if (id && !byId.has(id)) byId.set(id, r);
                    }

                    const sorted = Array.from(byId.values()).sort((a: any, b: any) => {
                        const ta = Date.parse((a as any)?.started ?? '') || 0;
                        const tb = Date.parse((b as any)?.started ?? '') || 0;
                        return tb - ta;
                    });

                    return sorted;
                })
            )
            .subscribe({
                next: (list) => {
                    const rows: ImagingStudyRow[] = (list ?? []).map((imagingStudy) => ({ imagingStudy }));
                    this.dataSource.data = rows;

                    this.dataSource.filterPredicate = (row: ImagingStudyRow, filter: string) => {
                        const img: any = row?.imagingStudy ?? {};
                        const hay = [
                            img?.description,
                            img?.status,
                            img?.started,
                            (img?.modality ?? []).map((m: any) => m?.code ?? m?.display).join(' '),
                            img?.id
                        ]
                            .filter(Boolean)
                            .join(' ')
                            .toLowerCase();
                        return hay.includes(filter);
                    };
                    this.applyFilter(this.filterText);

                    this.selection.clear();
                },
                error: () => {
                    this.dataSource.data = [];
                },
                complete: () => {
                    this.loading = false;
                }
            });
    }

    isAllSelected(): boolean {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.data.length;
        return numRows > 0 && numSelected === numRows;
    }

    toggleAllRows(): void {
        if (this.isAllSelected()) {
            this.selection.clear();
            return;
        }
        this.selection.select(...this.dataSource.data);
    }

    checkboxLabel(row?: ImagingStudyRow): string {
        if (!row) {
            return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row`;
    }

    imagingTitle(row: ImagingStudyRow): string {
        const img: any = row?.imagingStudy;
        return img?.description ?? `ImagingStudy ${(img?.id ?? '').toString()}`;
    }

    imagingModality(row: ImagingStudyRow): string {
        const img: any = row?.imagingStudy;
        const mods = (img?.modality ?? []) as any[];
        if (!mods.length) return '—';
        return mods
            .map((m: any) => m?.display ?? m?.code)
            .filter(Boolean)
            .join(', ') || '—';
    }

    imagingStarted(row: ImagingStudyRow): string {
        const img: any = row?.imagingStudy;
        return img?.started ?? '—';
    }
}
